import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import { 
  Home, MapPin, Clock, Package, Bell, Navigation, Info, ChevronDown, ChevronRight,
  Star, TrendingUp, CheckCircle, XCircle, Calendar, Filter, X, Zap,
  LogIn, LogOut, FileText, Car, AlertCircle, RefreshCw, MapPinned, Camera,
  Play, Square, Gauge, Wrench, Settings, CircleDot, ClipboardList, Plus, Eye
} from "lucide-react";
import { toast } from "sonner";

// Tab definitions
const TABS = [
  { id: "home", icon: Home, label: "Home" },
  { id: "work", icon: MapPin, label: "Work" },
  { id: "survey", icon: ClipboardList, label: "Survey" },  // Will be shown conditionally
  { id: "time", icon: Clock, label: "Time" },
  { id: "stock", icon: Package, label: "Stock" }
];

// Period filter options
const PERIODS = [
  { id: "today", label: "Today" },
  { id: "week", label: "This Week" },
  { id: "month", label: "This Month" },
  { id: "custom", label: "Custom" }
];

// Status colors matching map pin colors
const STATUS_COLORS = {
  "SURVEY PENDING": { bg: "bg-gray-500", border: "border-l-4 border-gray-500", text: "text-gray-600", hex: "#808080" },
  "NEEDS DTM": { bg: "bg-yellow-500", border: "border-l-4 border-yellow-500", text: "text-yellow-600", hex: "#FFD700" },
  "NEEDS DTC": { bg: "bg-orange-500", border: "border-l-4 border-orange-500", text: "text-orange-600", hex: "#FFA500" },
  "NEEDS MI": { bg: "bg-purple-500", border: "border-l-4 border-purple-500", text: "text-purple-600", hex: "#800080" },
  "COMPLETED": { bg: "bg-green-500", border: "border-l-4 border-green-500", text: "text-green-600", hex: "#008000" },
  "QC REJECTED": { bg: "bg-red-500", border: "border-l-4 border-red-500", text: "text-red-600", hex: "#FF0000" },
  "ON HOLD": { bg: "bg-red-600", border: "border-l-4 border-red-600", text: "text-red-700", hex: "#DC2626" },
  "PENDING": { bg: "bg-slate-400", border: "border-l-4 border-slate-400", text: "text-slate-600", hex: "#94A3B8" }
};

export default function TechnicianMobilePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("home");
  const [loading, setLoading] = useState(true);
  const [showPunchIn, setShowPunchIn] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  
  // Data
  const [stats, setStats] = useState({});
  const [points, setPoints] = useState({});
  const [quality, setQuality] = useState({});
  const [assignments, setAssignments] = useState([]);
  const [dtList, setDtList] = useState([]);
  const [attendance, setAttendance] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [travelLogs, setTravelLogs] = useState([]);
  const [activeTrip, setActiveTrip] = useState(null);
  const [permissions, setPermissions] = useState({ capabilities: {}, effective_tasks: [] });
  const [surveyData, setSurveyData] = useState({ assignments: [], feeders: [], dts: [] });
  const [workHistory, setWorkHistory] = useState([]);
  
  // Filters & Modals
  const [period, setPeriod] = useState("today");
  const [customDateRange, setCustomDateRange] = useState({ from: "", to: "" });
  const [showCustomDateModal, setShowCustomDateModal] = useState(false);
  const [showDTDetail, setShowDTDetail] = useState(null);
  const [mapExpanded, setMapExpanded] = useState(false);
  const [showStartTravel, setShowStartTravel] = useState(false);
  const [showEndTravel, setShowEndTravel] = useState(false);
  const [showTravelHistory, setShowTravelHistory] = useState(false);

  // Fetch all data
  const fetchData = async () => {
    setLoading(true);
    try {
      // Build stats URL based on period
      let statsUrl = `/work-activity/my-stats`;
      
      const [statsRes, pointsRes, assignRes, dtRes, attRes, invRes, notifRes, travelRes, permRes, surveyRes, historyRes] = await Promise.all([
        api.get(statsUrl).catch(() => ({ data: {} })),
        api.get(`/gamification/points/my-points?period=${period}`).catch(() => ({ data: {} })),
        api.get("/assignments/my-assignments").catch(() => ({ data: { assignments: [] } })),
        api.get("/dt-master?limit=100").catch(() => ({ data: { items: [] } })),
        api.get("/attendance/my-today").catch(() => ({ data: {} })),
        api.get("/inventory-issuance/my-inventory").catch(() => ({ data: { items: [] } })),
        api.get("/communications?type=announcement&limit=5").catch(() => ({ data: { items: [] } })),
        api.get("/travel/my-logs?date=" + new Date().toISOString().slice(0, 10)).catch(() => ({ data: { logs: [] } })),
        api.get("/employees/my-permissions").catch(() => ({ data: { capabilities: {}, effective_tasks: [] } })),
        api.get("/feeder-assignments/my-assignments").catch(() => ({ data: { assignments: [], feeders: [], dts: [] } })),
        api.get(`/work-activity/my-history?period=${period}&limit=50`).catch(() => ({ data: { items: [] } }))
      ]);
      
      // Map work-activity response to expected flat format based on period
      const periodMap = { today: "today", week: "week", month: "month", custom: "month" };
      const periodKey = periodMap[period] || "today";
      const rawStats = statsRes.data?.stats?.[periodKey] || {};
      setStats({
        ci_done: rawStats.ci || 0,
        rci_done: rawStats.rci || 0,
        dtm_done: rawStats.dtm || 0,
        dtc_done: rawStats.dtc || 0,
        mi_done: rawStats.mi || 0,
        total: rawStats.total || 0
      });
      setPoints(pointsRes.data || {});
      setAssignments(assignRes.data.assignments || []);
      setDtList(dtRes.data.items || []);
      setPermissions(permRes.data || { capabilities: {}, effective_tasks: [] });
      setSurveyData(surveyRes.data || { assignments: [], feeders: [], dts: [] });
      setWorkHistory(historyRes.data?.items || []);
      
      // Handle attendance data - extract nested attendance object
      const attData = attRes.data || {};
      setAttendance({
        ...attData.attendance,
        can_punch_in: attData.can_punch_in,
        can_punch_out: attData.can_punch_out
      });
      
      setInventory(invRes.data.items || []);
      setNotifications(notifRes.data.items || []);
      
      // Set travel logs and check for active trip
      const logs = travelRes.data.logs || travelRes.data.items || [];
      setTravelLogs(logs);
      const active = logs.find(l => l.start_time && !l.end_time);
      setActiveTrip(active || null);
      
      // Check if needs punch in (morning and not punched)
      const now = new Date();
      const hour = now.getHours();
      if (hour >= 6 && hour < 12 && attData.can_punch_in) {
        setShowPunchIn(true);
      }
      
      // Calculate quality stats
      const qcPassed = dtRes.data.items?.filter(d => d.qc_status === "passed").length || 0;
      const qcRejected = dtRes.data.items?.filter(d => d.qc_status === "rejected" || d.qc_status === "rework").length || 0;
      const total = qcPassed + qcRejected;
      setQuality({
        ftr: total > 0 ? Math.round((qcPassed / total) * 100) : 100,
        passed: qcPassed,
        rejected: qcRejected
      });
      
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [period, customDateRange]);

  // Helper functions - must be defined before useMemo
  const getRequiredStage = (status) => {
    if (!status || status === "pending" || status === "new") return "SURVEY PENDING";
    if (status === "ci_done") return "NEEDS DTM";
    if (status === "dtm_done") return "NEEDS DTC";
    if (status === "dtc_done") return "NEEDS MI";
    if (status === "mi_done" || status === "qc_passed" || status === "paid") return "COMPLETED";
    if (status === "qc_rejected" || status === "rework") return "QC REJECTED";
    if (status?.includes("hold")) return "ON HOLD";
    return "PENDING";
  };

  const getStageColors = (stage) => {
    return STATUS_COLORS[stage] || STATUS_COLORS["PENDING"];
  };

  const getNextAction = (status) => {
    if (!status || status === "pending" || status === "new") return { label: "Do Survey", route: "survey" };
    if (status === "ci_done") return { label: "Do DTM", route: "dtm" };
    if (status === "dtm_done") return { label: "Do DTC", route: "dtc" };
    if (status === "dtc_done") return { label: "Do MI", route: "mi" };
    return null;
  };

  // Group DTs by Feeder then by Status
  const groupedDTs = useMemo(() => {
    const byFeeder = {};
    dtList.forEach(dt => {
      const feeder = dt.feeder_code || "Unassigned";
      if (!byFeeder[feeder]) byFeeder[feeder] = {};
      
      const stage = getRequiredStage(dt.lifecycle_status);
      if (!byFeeder[feeder][stage]) byFeeder[feeder][stage] = [];
      byFeeder[feeder][stage].push(dt);
    });
    return byFeeder;
  }, [dtList]);

  const [punchLoading, setPunchLoading] = useState(false);

  // Handlers
  const handlePunchIn = async () => {
    setPunchLoading(true);
    toast.info("Capturing location...");
    
    // Check if geolocation is available
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      setPunchLoading(false);
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await api.post("/attendance/punch-in", {
            gps_lat: pos.coords.latitude,
            gps_lng: pos.coords.longitude
          });
          toast.success("Punched In!");
          setShowPunchIn(false);
          fetchData();
        } catch (e) {
          toast.error(e.response?.data?.detail || "Punch in failed");
        } finally {
          setPunchLoading(false);
        }
      }, 
      (err) => {
        console.error("Geolocation error:", err);
        setPunchLoading(false);
        if (err.code === 1) {
          toast.error("Location permission denied. Please enable in browser settings.");
        } else if (err.code === 2) {
          toast.error("Location unavailable. Please try again.");
        } else if (err.code === 3) {
          toast.error("Location request timed out. Please try again.");
        } else {
          toast.error("Could not get location. Please try again.");
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const handlePunchOut = async () => {
    setPunchLoading(true);
    toast.info("Capturing location...");
    
    // Check if geolocation is available
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      setPunchLoading(false);
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await api.post("/attendance/punch-out", {
            gps_lat: pos.coords.latitude,
            gps_lng: pos.coords.longitude
          });
          toast.success("Punched Out!");
          fetchData();
        } catch (e) {
          toast.error(e.response?.data?.detail || "Punch out failed");
        } finally {
          setPunchLoading(false);
        }
      },
      (err) => {
        console.error("Geolocation error:", err);
        setPunchLoading(false);
        if (err.code === 1) {
          toast.error("Location permission denied. Please enable in browser settings.");
        } else if (err.code === 2) {
          toast.error("Location unavailable. Please try again.");
        } else if (err.code === 3) {
          toast.error("Location request timed out. Please try again.");
        } else {
          toast.error("Could not get location. Please try again.");
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const openNavigation = (dt) => {
    if (dt.gps_lat && dt.gps_lng) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${dt.gps_lat},${dt.gps_lng}`, "_blank");
    } else {
      toast.error("No GPS coordinates for this DT");
    }
  };

  const handleCustomDateSubmit = () => {
    if (customDateRange.from && customDateRange.to) {
      setPeriod("custom");
      setShowCustomDateModal(false);
    } else {
      toast.error("Please select both dates");
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 pb-20" data-testid="tech-mobile-page">
      {/* Floating Punch In Button */}
      {showPunchIn && (
        <button
          onClick={handlePunchIn}
          className="fixed top-4 right-4 z-50 px-4 py-3 bg-green-500 text-white rounded-full shadow-lg flex items-center gap-2 animate-bounce"
          data-testid="floating-punch-in"
        >
          <LogIn size={20} />
          <span className="font-bold">Punch In</span>
        </button>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-4 sticky top-0 z-40">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-lg font-bold">Hi, {user?.full_name?.split(" ")[0] || "Technician"}! 👋</div>
            <div className="text-xs text-orange-100">
              {new Date().toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}
            </div>
          </div>
          <button 
            onClick={() => setShowNotifications(true)}
            className="relative p-2 bg-white/20 rounded-full"
          >
            <Bell size={20} />
            {notifications.length > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-[10px] flex items-center justify-center font-bold">
                {notifications.length}
              </span>
            )}
          </button>
          <button 
            onClick={() => { logout(); navigate("/login"); }}
            className="p-2 bg-white/20 rounded-full"
            data-testid="tech-logout-btn"
          >
            <LogOut size={18} />
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="px-4 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw size={32} className="animate-spin text-orange-500" />
          </div>
        ) : (
          <>
            {/* HOME TAB */}
            {activeTab === "home" && (
              <div className="space-y-4 animate-fade-up">
                {/* Period Filter */}
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {PERIODS.map(p => (
                    <button
                      key={p.id}
                      onClick={() => {
                        if (p.id === "custom") {
                          setShowCustomDateModal(true);
                        } else {
                          setPeriod(p.id);
                          setCustomDateRange({ from: "", to: "" });
                        }
                      }}
                      className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition ${
                        period === p.id 
                          ? "bg-orange-500 text-white" 
                          : "bg-white text-slate-600 border border-slate-200"
                      }`}
                      data-testid={`period-filter-${p.id}`}
                    >
                      {p.id === "custom" && customDateRange.from ? 
                        `${customDateRange.from} → ${customDateRange.to}` : 
                        p.label}
                    </button>
                  ))}
                </div>

                {/* Quick Stats */}
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="text-xs font-bold text-slate-500 uppercase mb-3">
                    Work Stats {period === "custom" && customDateRange.from && (
                      <span className="text-orange-500 normal-case">({customDateRange.from} to {customDateRange.to})</span>
                    )}
                  </div>
                  <div className="grid grid-cols-5 gap-2 text-center">
                    {[
                      { label: "CI", value: stats.ci_done || 0, color: "text-slate-600" },
                      { label: "RCI", value: stats.rci_done || 0, color: "text-cyan-600" },
                      { label: "DTM", value: stats.dtm_done || 0, color: "text-yellow-600" },
                      { label: "DTC", value: stats.dtc_done || 0, color: "text-orange-600" },
                      { label: "MI", value: stats.mi_done || 0, color: "text-green-600" }
                    ].map(s => (
                      <div key={s.label} className="bg-slate-50 rounded-lg py-3">
                        <div className={`text-xl font-black ${s.color}`}>{s.value}</div>
                        <div className="text-[10px] text-slate-500 font-medium">{s.label}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Points */}
                <div className="bg-gradient-to-r from-yellow-400 to-orange-400 rounded-xl p-4 shadow-sm text-white">
                  <div className="flex items-center gap-2 mb-3">
                    <Star size={16} fill="white" />
                    <span className="text-xs font-bold uppercase">Points Earned</span>
                  </div>
                  <div className="text-3xl font-black">
                    +{points.period_points || points.total || 0}
                    <span className="text-sm font-normal ml-2">pts</span>
                  </div>
                  {points.streak && (
                    <div className="mt-2 text-sm">🔥 {points.streak}-day streak!</div>
                  )}
                </div>

                {/* Work History */}
                <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="work-history-panel">
                  <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Clock size={16} className="text-orange-500" />
                      <span className="text-xs font-bold text-slate-500 uppercase">Work History</span>
                    </div>
                    <span className="text-xs font-bold text-orange-500">{workHistory.length} tasks</span>
                  </div>
                  {workHistory.length > 0 ? (
                    <div className="divide-y divide-slate-50 max-h-64 overflow-y-auto">
                      {workHistory.map((item) => {
                        const typeColors = {
                          survey_submitted: "bg-blue-100 text-blue-700",
                          resurvey_submitted: "bg-cyan-100 text-cyan-700",
                          dtm_completed: "bg-yellow-100 text-yellow-700",
                          dtc_completed: "bg-orange-100 text-orange-700",
                          mi_completed: "bg-green-100 text-green-700"
                        };
                        const badgeClass = typeColors[item.event_type] || "bg-slate-100 text-slate-600";
                        const time = item.created_at ? new Date(item.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
                        return (
                          <div key={item.id} className="px-4 py-2.5 flex items-center gap-3" data-testid={`history-item-${item.id}`}>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold shrink-0 ${badgeClass}`}>{item.label}</span>
                                <span className="text-sm font-bold text-slate-800 truncate">{item.dt_code}</span>
                              </div>
                              <div className="text-[11px] text-slate-400 mt-0.5 truncate">
                                {item.dt_name} &bull; {item.feeder_code}
                              </div>
                            </div>
                            <div className="text-[10px] text-slate-400 whitespace-nowrap shrink-0">{time}</div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-6 text-center text-sm text-slate-400">No work recorded for this period</div>
                  )}
                </div>

                {/* Quality Performance */}
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="text-xs font-bold text-slate-500 uppercase mb-3">Quality (FTR)</div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-3xl font-black text-slate-800">{quality.ftr}%</div>
                      <div className="text-xs text-slate-500">First Time Right</div>
                    </div>
                    <div className="flex gap-4">
                      <div className="text-center">
                        <div className="flex items-center gap-1 text-green-600">
                          <CheckCircle size={16} />
                          <span className="text-xl font-bold">{quality.passed}</span>
                        </div>
                        <div className="text-[10px] text-slate-500">Passed</div>
                      </div>
                      <div className="text-center">
                        <div className="flex items-center gap-1 text-red-600">
                          <XCircle size={16} />
                          <span className="text-xl font-bold">{quality.rejected}</span>
                        </div>
                        <div className="text-[10px] text-slate-500">Rejected</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Feeders Assigned Panel */}
                <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="feeders-assigned-panel">
                  <div className="px-4 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white">
                    <div className="flex items-center gap-2">
                      <Zap size={18} />
                      <span className="font-bold">Feeders Assigned</span>
                      <span className="ml-auto bg-white/20 px-2 py-0.5 rounded-full text-xs font-semibold">
                        {assignments.length} {assignments.length === 1 ? 'Feeder' : 'Feeders'}
                      </span>
                    </div>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {assignments.length > 0 ? (
                      assignments.map((assignment) => (
                        <div key={assignment.id} className="p-4" data-testid={`assigned-feeder-${assignment.feeder_code}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                <span className="font-bold text-slate-800">{assignment.feeder_name || assignment.feeder_code}</span>
                              </div>
                              <div className="mt-1 text-xs text-slate-500">
                                <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">{assignment.feeder_code}</span>
                                <span className="mx-2">•</span>
                                <span>{assignment.circle_name}</span>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-lg font-bold text-slate-800">{assignment.dt_count || 0}</div>
                              <div className="text-[10px] text-slate-500">DTs</div>
                            </div>
                          </div>
                          <div className="mt-2 flex items-center gap-4 text-xs text-slate-500">
                            <div className="flex items-center gap-1">
                              <Calendar size={12} />
                              <span>
                                {assignment.duration_type === 'saturation' ? 'Till Saturation' : 
                                  `${new Date(assignment.start_date).toLocaleDateString()} - ${new Date(assignment.end_date).toLocaleDateString()}`}
                              </span>
                            </div>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                              assignment.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                            }`}>
                              {assignment.status}
                            </span>
                          </div>
                          {/* Allowed Tasks */}
                          {assignment.allowed_tasks && assignment.allowed_tasks.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              <span className="text-[10px] text-slate-400 mr-1">Tasks:</span>
                              {assignment.allowed_tasks.map(task => (
                                <span key={task} className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded text-[10px] font-semibold uppercase">
                                  {task}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="p-6 text-center text-slate-400">
                        <Zap size={32} className="mx-auto mb-2 opacity-50" />
                        <div className="text-sm">No feeders assigned yet</div>
                        <div className="text-xs mt-1">Contact your supervisor for assignments</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* WORK TAB */}
            {activeTab === "work" && (
              <div className="space-y-4 animate-fade-up">
                {/* Mini Map */}
                <div 
                  className={`bg-white rounded-xl overflow-hidden shadow-sm transition-all ${
                    mapExpanded ? "h-80" : "h-32"
                  }`}
                  onClick={() => setMapExpanded(!mapExpanded)}
                >
                  <div className="h-full bg-gradient-to-br from-blue-100 to-green-100 flex items-center justify-center relative">
                    <MapPinned size={mapExpanded ? 48 : 24} className="text-blue-500" />
                    <div className="absolute bottom-2 right-2 bg-white/80 px-2 py-1 rounded text-xs text-slate-600">
                      {mapExpanded ? "Tap to collapse" : "Tap to expand map"}
                    </div>
                    {!mapExpanded && (
                      <div className="absolute top-2 left-2 bg-orange-500 text-white px-2 py-1 rounded text-xs font-bold">
                        {dtList.length} DTs
                      </div>
                    )}
                  </div>
                </div>

                {/* Status Legend */}
                <div className="bg-white rounded-xl p-3 shadow-sm">
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(STATUS_COLORS).slice(0, 6).map(([status, colors]) => (
                      <div key={status} className="flex items-center gap-1.5">
                        <div className={`w-3 h-3 rounded-full ${colors.bg}`} />
                        <span className="text-[10px] text-slate-600">{status}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Assigned Feeders Summary */}
                {assignments.length > 0 && (
                  <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="work-tab-feeders">
                    <div className="px-4 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Zap size={16} />
                          <span className="font-bold text-sm">My Assigned Feeders</span>
                        </div>
                        <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">
                          {assignments.length} {assignments.length === 1 ? 'Feeder' : 'Feeders'}
                        </span>
                      </div>
                    </div>
                    <div className="p-3 flex flex-wrap gap-2">
                      {assignments.map((a) => (
                        <div 
                          key={a.id}
                          className="flex items-center gap-2 bg-blue-50 border border-blue-200 px-3 py-2 rounded-lg"
                          data-testid={`work-feeder-badge-${a.feeder_code}`}
                        >
                          <div className="w-2 h-2 rounded-full bg-green-500"></div>
                          <div>
                            <div className="text-sm font-semibold text-blue-800">{a.feeder_name || a.feeder_code}</div>
                            <div className="text-[10px] text-blue-600">{a.dt_count || 0} DTs • {a.circle_name}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* DT List Grouped */}
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
                    <div className="text-sm font-bold text-slate-700">My DTs</div>
                  </div>
                  
                  <div className="divide-y divide-slate-100 max-h-[400px] overflow-y-auto">
                    {Object.entries(groupedDTs).map(([feeder, stages]) => (
                      <FeederGroup 
                        key={feeder} 
                        feeder={feeder} 
                        stages={stages}
                        onNavigate={openNavigation}
                        onInfo={setShowDTDetail}
                        getStageColors={getStageColors}
                        getNextAction={getNextAction}
                        navigate={navigate}
                        effectiveTasks={permissions.effective_tasks}
                        userRole={permissions.role || user?.role}
                      />
                    ))}
                    
                    {Object.keys(groupedDTs).length === 0 && (
                      <div className="p-8 text-center text-slate-400">
                        <MapPin size={32} className="mx-auto mb-2" />
                        <div className="font-medium">No DTs in your feeders yet</div>
                        {assignments.length > 0 && (
                          <div className="text-xs mt-1">
                            You have {assignments.length} feeder(s) assigned but no DTs have been added to them
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* SURVEY TAB */}
            {activeTab === "survey" && (
              <div className="space-y-4 animate-fade-up" data-testid="survey-tab-content">
                {/* Survey Stats */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-white rounded-xl p-3 shadow-sm text-center">
                    <div className="text-2xl font-black text-slate-800">{surveyData.dts?.length || 0}</div>
                    <div className="text-[10px] text-slate-500 font-semibold">Total DTs</div>
                  </div>
                  <div className="bg-white rounded-xl p-3 shadow-sm text-center">
                    <div className="text-2xl font-black text-green-600">{(surveyData.dts || []).filter(d => d.has_survey).length}</div>
                    <div className="text-[10px] text-slate-500 font-semibold">Surveyed</div>
                  </div>
                  <div className="bg-white rounded-xl p-3 shadow-sm text-center">
                    <div className="text-2xl font-black text-orange-600">{(surveyData.dts || []).filter(d => !d.has_survey).length}</div>
                    <div className="text-[10px] text-slate-500 font-semibold">Pending</div>
                  </div>
                </div>

                {/* Assigned Feeders for Survey */}
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <div className="px-4 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <ClipboardList size={18} />
                        <span className="font-bold">Survey Assignments</span>
                      </div>
                      <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">
                        {surveyData.assignments?.length || 0} Feeders
                      </span>
                    </div>
                  </div>

                  <div className="divide-y divide-slate-100">
                    {(surveyData.assignments || []).map(assignment => {
                      const feederDTs = (surveyData.dts || []).filter(dt => dt.feeder_code === assignment.feeder_code);
                      const surveyedCount = feederDTs.filter(d => d.has_survey).length;
                      const pendingCount = feederDTs.length - surveyedCount;
                      const allowsSurvey = !assignment.allowed_tasks || assignment.allowed_tasks.includes("survey");

                      if (!allowsSurvey) return null;

                      return (
                        <div key={assignment.id} className="p-4" data-testid={`survey-feeder-${assignment.feeder_code}`}>
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <div className="font-bold text-slate-800">{assignment.feeder_name || assignment.feeder_code}</div>
                              <div className="text-xs text-slate-500 flex items-center gap-2 mt-1">
                                <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">{assignment.feeder_code}</span>
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                  assignment.status === "in_progress" ? "bg-blue-100 text-blue-700" :
                                  assignment.status === "assigned" ? "bg-yellow-100 text-yellow-700" :
                                  "bg-green-100 text-green-700"
                                }`}>
                                  {assignment.status}
                                </span>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-slate-500">{feederDTs.length} DTs</div>
                              <div className="flex items-center gap-2 text-[10px] mt-1">
                                <span className="text-green-600 font-semibold">{surveyedCount} Done</span>
                                <span className="text-orange-600 font-semibold">{pendingCount} Pending</span>
                              </div>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex gap-2">
                            <button
                              onClick={() => navigate(`/survey/new?feeder=${assignment.feeder_code}`)}
                              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-cyan-500 text-white rounded-lg text-sm font-semibold hover:bg-cyan-600"
                              data-testid={`new-survey-btn-${assignment.feeder_code}`}
                            >
                              <Plus size={16} />
                              New Survey
                            </button>
                            <button
                              onClick={() => navigate(`/dt-master?feeder=${assignment.feeder_code}`)}
                              className="flex items-center justify-center gap-1.5 px-3 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-semibold hover:bg-slate-200"
                              data-testid={`view-dts-btn-${assignment.feeder_code}`}
                            >
                              <Eye size={16} />
                              View
                            </button>
                          </div>

                          {/* DTs Preview (pending surveys) */}
                          {pendingCount > 0 && (
                            <div className="mt-3 pt-3 border-t border-slate-100">
                              <div className="text-[10px] text-slate-500 font-semibold mb-2">Pending Surveys:</div>
                              <div className="flex flex-wrap gap-1">
                                {feederDTs.filter(d => !d.has_survey).slice(0, 5).map(dt => (
                                  <button
                                    key={dt.id || dt.dt_number}
                                    onClick={() => navigate(`/survey/${dt.id}`)}
                                    className="px-2 py-1 bg-orange-50 text-orange-700 rounded text-[10px] font-semibold hover:bg-orange-100"
                                  >
                                    {dt.dt_number}
                                  </button>
                                ))}
                                {pendingCount > 5 && (
                                  <span className="px-2 py-1 text-slate-400 text-[10px]">+{pendingCount - 5} more</span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {(surveyData.assignments || []).length === 0 && (
                      (() => {
                        // Show feeders from technician_assignments if no feeder_assignments
                        const techFeeders = [...new Set((assignments || []).flatMap(a => a.feeder_ids || []))];
                        if (techFeeders.length === 0) return (
                          <div className="p-8 text-center text-slate-400">
                            <ClipboardList size={32} className="mx-auto mb-2 opacity-50" />
                            <div className="text-sm">No survey assignments</div>
                            <div className="text-xs mt-1">You'll see your survey feeders here</div>
                          </div>
                        );
                        return techFeeders.map(feederCode => {
                          const feederDTs = (dtList || []).filter(dt => dt.feeder_code === feederCode);
                          const surveyedCount = feederDTs.filter(d => d.status !== "new").length;
                          const pendingCount = feederDTs.filter(d => d.status === "new").length;
                          return (
                            <div key={feederCode} className="p-4" data-testid={`survey-feeder-${feederCode}`}>
                              <div className="flex items-start justify-between mb-3">
                                <div>
                                  <div className="font-bold text-slate-800">{feederCode}</div>
                                  <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{feederCode}</span>
                                </div>
                                <div className="text-right">
                                  <div className="text-xs text-slate-500">{feederDTs.length} DTs</div>
                                  <div className="flex items-center gap-2 text-[10px] mt-1">
                                    <span className="text-green-600 font-semibold">{surveyedCount} Done</span>
                                    <span className="text-orange-600 font-semibold">{pendingCount} Pending</span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => navigate(`/survey/new?feeder=${feederCode}`)}
                                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-cyan-500 text-white rounded-lg text-sm font-semibold hover:bg-cyan-600"
                                  data-testid={`new-survey-btn-${feederCode}`}
                                >
                                  <Plus size={16} />
                                  New Survey
                                </button>
                                <button
                                  onClick={() => navigate(`/dt-master?feeder=${feederCode}`)}
                                  className="flex items-center justify-center gap-1.5 px-3 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-semibold hover:bg-slate-200"
                                  data-testid={`view-dts-btn-${feederCode}`}
                                >
                                  <Eye size={16} />
                                  View
                                </button>
                              </div>
                              {pendingCount > 0 && (
                                <div className="mt-3 pt-3 border-t border-slate-100">
                                  <div className="text-[10px] text-slate-500 font-semibold mb-2">Pending Surveys:</div>
                                  <div className="flex flex-wrap gap-1">
                                    {feederDTs.filter(d => d.status === "new").slice(0, 5).map(dt => (
                                      <button key={dt.id} onClick={() => navigate(`/survey/${dt.id}`)}
                                        className="px-2 py-1 bg-orange-50 text-orange-700 rounded text-[10px] font-semibold hover:bg-orange-100">
                                        {dt.dt_code}
                                      </button>
                                    ))}
                                    {pendingCount > 5 && <span className="px-2 py-1 text-slate-400 text-[10px]">+{pendingCount - 5} more</span>}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        });
                      })()
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* TIME TAB */}
            {activeTab === "time" && (
              <div className="space-y-4 animate-fade-up">
                {/* Today's Attendance */}
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="text-xs font-bold text-slate-500 uppercase mb-3">Today</div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className={`p-4 rounded-xl text-center ${attendance?.punch_in_time ? "bg-green-50" : "bg-slate-50"}`}>
                      <LogIn size={24} className={`mx-auto mb-2 ${attendance?.punch_in_time ? "text-green-600" : "text-slate-400"}`} />
                      <div className="text-xs text-slate-500">Punch In</div>
                      <div className="text-lg font-bold text-slate-800">
                        {attendance?.punch_in_time ? new Date(attendance.punch_in_time).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }) : "--:--"}
                      </div>
                    </div>
                    <div className={`p-4 rounded-xl text-center ${attendance?.punch_out_time ? "bg-red-50" : "bg-slate-50"}`}>
                      <LogOut size={24} className={`mx-auto mb-2 ${attendance?.punch_out_time ? "text-red-600" : "text-slate-400"}`} />
                      <div className="text-xs text-slate-500">Punch Out</div>
                      <div className="text-lg font-bold text-slate-800">
                        {attendance?.punch_out_time ? new Date(attendance.punch_out_time).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }) : "--:--"}
                      </div>
                    </div>
                  </div>

                  {/* Punch Button */}
                  {attendance?.can_punch_out && (
                    <button
                      onClick={handlePunchOut}
                      disabled={punchLoading}
                      className="w-full mt-4 py-3 bg-red-500 text-white rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="punch-out-btn"
                    >
                      {punchLoading ? (
                        <RefreshCw size={20} className="animate-spin" />
                      ) : (
                        <LogOut size={20} />
                      )}
                      {punchLoading ? "Getting Location..." : "Punch Out"}
                    </button>
                  )}
                  {attendance?.can_punch_in && (
                    <button
                      onClick={handlePunchIn}
                      disabled={punchLoading}
                      className="w-full mt-4 py-3 bg-green-500 text-white rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="punch-in-btn"
                    >
                      {punchLoading ? (
                        <RefreshCw size={20} className="animate-spin" />
                      ) : (
                        <LogIn size={20} />
                      )}
                      {punchLoading ? "Getting Location..." : "Punch In"}
                    </button>
                  )}
                </div>

                {/* This Month Summary */}
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="text-xs font-bold text-slate-500 uppercase mb-3">This Month</div>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="bg-green-50 rounded-lg py-3">
                      <div className="text-xl font-bold text-green-600">{attendance?.month_present || 0}</div>
                      <div className="text-[10px] text-slate-500">Present</div>
                    </div>
                    <div className="bg-yellow-50 rounded-lg py-3">
                      <div className="text-xl font-bold text-yellow-600">{attendance?.month_leave || 0}</div>
                      <div className="text-[10px] text-slate-500">Leave</div>
                    </div>
                    <div className="bg-red-50 rounded-lg py-3">
                      <div className="text-xl font-bold text-red-600">{attendance?.month_absent || 0}</div>
                      <div className="text-[10px] text-slate-500">Absent</div>
                    </div>
                  </div>
                </div>

                {/* Today's Travel - Distance Only (No TA Earned) */}
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Car size={16} className="text-blue-500" />
                      <span className="text-xs font-bold text-slate-500 uppercase">Today's Travel</span>
                    </div>
                    {activeTrip && (
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-[10px] font-bold rounded-full animate-pulse">
                        TRIP ACTIVE
                      </span>
                    )}
                  </div>
                  
                  <div className="text-center mb-4">
                    <div className="text-3xl font-black text-slate-800">{attendance?.distance_km || 0} km</div>
                    <div className="text-xs text-slate-500">Distance covered today</div>
                  </div>

                  {/* Travel Log Actions */}
                  <div className="space-y-3">
                    {!activeTrip ? (
                      <button
                        onClick={() => setShowStartTravel(true)}
                        className="w-full py-3 bg-blue-500 text-white rounded-xl font-bold flex items-center justify-center gap-2"
                        data-testid="start-travel-btn"
                      >
                        <Play size={20} />
                        Start Travel
                      </button>
                    ) : (
                      <button
                        onClick={() => setShowEndTravel(true)}
                        className="w-full py-3 bg-orange-500 text-white rounded-xl font-bold flex items-center justify-center gap-2"
                        data-testid="end-travel-btn"
                      >
                        <Square size={20} />
                        End Travel
                      </button>
                    )}
                  </div>

                  {/* Today's Trips */}
                  {travelLogs.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <div className="text-xs font-bold text-slate-500 uppercase mb-2">Today's Trips</div>
                      <div className="space-y-2">
                        {travelLogs.map((log, i) => (
                          <div key={i} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-2">
                              <Car size={14} className="text-slate-400" />
                              <span className="text-sm font-medium text-slate-700">{log.vehicle_number || "—"}</span>
                            </div>
                            <div className="text-xs text-slate-500">
                              {log.start_km} → {log.end_km || "..."} km
                            </div>
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                              log.end_time ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"
                            }`}>
                              {log.end_time ? "Done" : "Active"}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-2 gap-3">
                  <button 
                    onClick={() => navigate("/leave")}
                    className="bg-white rounded-xl p-4 shadow-sm text-center hover:bg-slate-50"
                  >
                    <FileText size={24} className="mx-auto mb-2 text-purple-500" />
                    <div className="text-sm font-semibold text-slate-700">Apply Leave</div>
                  </button>
                  <button 
                    onClick={() => setShowTravelHistory(true)}
                    className="bg-white rounded-xl p-4 shadow-sm text-center hover:bg-slate-50"
                  >
                    <Calendar size={24} className="mx-auto mb-2 text-blue-500" />
                    <div className="text-sm font-semibold text-slate-700">Travel History</div>
                  </button>
                </div>
              </div>
            )}

            {/* STOCK TAB */}
            {activeTab === "stock" && (
              <div className="space-y-4 animate-fade-up">
                {/* Inventory Summary */}
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="text-xs font-bold text-slate-500 uppercase mb-3">My Inventory</div>
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div className="bg-blue-50 rounded-lg py-4">
                      <div className="text-2xl font-bold text-blue-600">
                        {inventory.filter(i => i.item_type === "meter").length}
                      </div>
                      <div className="text-xs text-slate-500">Meters</div>
                    </div>
                    <div className="bg-green-50 rounded-lg py-4">
                      <div className="text-2xl font-bold text-green-600">
                        {inventory.filter(i => i.item_type !== "meter").length}
                      </div>
                      <div className="text-xs text-slate-500">Materials</div>
                    </div>
                  </div>
                </div>

                {/* Inventory List */}
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
                    <div className="text-sm font-bold text-slate-700">Items</div>
                  </div>
                  <div className="divide-y divide-slate-100 max-h-[400px] overflow-y-auto">
                    {inventory.length === 0 ? (
                      <div className="p-8 text-center text-slate-400">
                        <Package size={32} className="mx-auto mb-2" />
                        <div>No inventory issued</div>
                      </div>
                    ) : (
                      inventory.map((item, i) => (
                        <div key={i} className="px-4 py-3 flex items-center justify-between">
                          <div>
                            <div className="font-semibold text-slate-800 text-sm">{item.serial_number || item.item_name}</div>
                            <div className="text-xs text-slate-500">{item.item_type}</div>
                          </div>
                          <span className={`px-2 py-1 rounded text-[10px] font-bold ${
                            item.status === "available" ? "bg-green-100 text-green-700" :
                            item.status === "installed" ? "bg-blue-100 text-blue-700" :
                            "bg-slate-100 text-slate-600"
                          }`}>
                            {item.status}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-4 py-2 z-50">
        <div className="flex justify-around">
          {TABS.filter(tab => {
            // Show Survey tab if user has survey permission
            if (tab.id === "survey") {
              return permissions.effective_tasks?.includes("survey");
            }
            return true;
          }).map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex flex-col items-center py-2 px-4 rounded-lg transition ${
                activeTab === tab.id 
                  ? "text-orange-500 bg-orange-50" 
                  : "text-slate-500"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon size={24} />
              <span className="text-xs font-medium mt-1">{tab.label}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* Custom Date Range Modal */}
      {showCustomDateModal && (
        <CustomDateModal
          dateRange={customDateRange}
          setDateRange={setCustomDateRange}
          onSubmit={handleCustomDateSubmit}
          onClose={() => setShowCustomDateModal(false)}
        />
      )}

      {/* DT Detail Modal */}
      {showDTDetail && (
        <DTDetailModal 
          dt={showDTDetail} 
          onClose={() => setShowDTDetail(null)} 
        />
      )}

      {/* Notifications Modal */}
      {showNotifications && (
        <NotificationsModal 
          notifications={notifications}
          onClose={() => setShowNotifications(false)}
        />
      )}

      {/* Start Travel Modal */}
      {showStartTravel && (
        <StartTravelModal
          onClose={() => setShowStartTravel(false)}
          onSuccess={() => {
            setShowStartTravel(false);
            fetchData();
          }}
        />
      )}

      {/* End Travel Modal */}
      {showEndTravel && activeTrip && (
        <EndTravelModal
          trip={activeTrip}
          onClose={() => setShowEndTravel(false)}
          onSuccess={() => {
            setShowEndTravel(false);
            fetchData();
          }}
        />
      )}

      {/* Travel History Modal */}
      {showTravelHistory && (
        <TravelHistoryModal
          onClose={() => setShowTravelHistory(false)}
        />
      )}
    </div>
  );
}

// Custom Date Range Modal
function CustomDateModal({ dateRange, setDateRange, onSubmit, onClose }) {
  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-sm rounded-2xl overflow-hidden animate-scale-up">
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
          <div className="font-bold text-slate-800">Select Date Range</div>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full">
            <X size={20} />
          </button>
        </div>
        
        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">From Date</label>
            <input
              type="date"
              value={dateRange.from}
              onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              data-testid="custom-from-date"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">To Date</label>
            <input
              type="date"
              value={dateRange.to}
              onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              data-testid="custom-to-date"
            />
          </div>
          
          <button
            onClick={onSubmit}
            className="w-full py-3 bg-orange-500 text-white rounded-xl font-bold"
            data-testid="apply-date-range-btn"
          >
            Apply Date Range
          </button>
        </div>
      </div>
    </div>
  );
}

// Feeder Group Component
function FeederGroup({ feeder, stages, onNavigate, onInfo, getStageColors, getNextAction, navigate, effectiveTasks, userRole }) {
  const [expanded, setExpanded] = useState(true);
  const [expandedStages, setExpandedStages] = useState({});
  
  const totalDTs = Object.values(stages).flat().length;
  
  const toggleStage = (stage) => {
    setExpandedStages(prev => ({ ...prev, [stage]: !prev[stage] }));
  };

  // Stage order for consistent display
  const stageOrder = ["SURVEY PENDING", "NEEDS DTM", "NEEDS DTC", "NEEDS MI", "COMPLETED", "QC REJECTED", "ON HOLD"];

  return (
    <div className="border-b border-slate-100 last:border-b-0">
      {/* Feeder Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center justify-between bg-slate-50 hover:bg-slate-100"
      >
        <div className="flex items-center gap-2">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <span className="font-bold text-sm text-slate-700">{feeder}</span>
        </div>
        <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium">
          {totalDTs} DTs
        </span>
      </button>
      
      {/* Stages */}
      {expanded && (
        <div className="pl-2">
          {stageOrder.filter(stage => stages[stage]?.length > 0).map((stage) => {
            const dts = stages[stage];
            const colors = getStageColors(stage);
            
            return (
              <div key={stage} className={`${colors.border} bg-white my-1 mx-2 rounded-r-lg overflow-hidden`}>
                {/* Stage Header */}
                <button
                  onClick={() => toggleStage(stage)}
                  className="w-full px-3 py-2 flex items-center justify-between text-left hover:bg-slate-50"
                >
                  <div className="flex items-center gap-2">
                    {expandedStages[stage] !== false ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    <div className={`w-3 h-3 rounded-full ${colors.bg}`} />
                    <span className={`text-xs font-bold ${colors.text}`}>{stage}</span>
                  </div>
                  <span className="text-[10px] text-slate-400 font-medium">{dts.length} DTs</span>
                </button>
                
                {/* DTs in Stage */}
                {expandedStages[stage] !== false && (
                  <div className="pb-2">
                    {dts.map(dt => {
                      const nextAction = getNextAction(dt.lifecycle_status);
                      const capacity = dt.transformer_capacity_kva || dt.capacity_kva || dt.capacity;
                      return (
                        <div 
                          key={dt.id} 
                          className="flex items-center justify-between py-2 px-3 mx-2 hover:bg-slate-50 rounded"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm text-slate-800 truncate">{dt.dt_code}</span>
                              {capacity && (
                                <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded">
                                  {capacity} kVA
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-slate-500 truncate">{dt.dt_name || dt.location_name || "—"}</div>
                          </div>
                          <div className="flex items-center gap-1 ml-2">
                            {nextAction && (() => {
                              // Admin/supervisor bypass — show all actions
                              const isAdmin = ["admin", "supervisor", "ci", "package_head"].includes(userRole);
                              const hasPermission = isAdmin || !effectiveTasks?.length || effectiveTasks.includes(nextAction.route);
                              return hasPermission ? (
                                <button
                                  onClick={() => navigate(`/${nextAction.route}/${dt.id}`)}
                                  className={`px-2 py-1 text-[10px] font-bold rounded ${colors.bg} text-white hover:opacity-90`}
                                  data-testid={`action-${nextAction.route}-${dt.id}`}
                                >
                                  {nextAction.label}
                                </button>
                              ) : null;
                            })()}
                            <button
                              onClick={() => onNavigate(dt)}
                              className="p-1.5 text-blue-500 hover:bg-blue-50 rounded"
                              title="Navigate"
                            >
                              <Navigation size={16} />
                            </button>
                            <button
                              onClick={() => onInfo(dt)}
                              className="p-1.5 text-slate-500 hover:bg-slate-100 rounded"
                              title="Details"
                            >
                              <Info size={16} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// DT Detail Modal
function DTDetailModal({ dt, onClose }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await api.get(`/dt-master/${dt.id}`);
        setHistory(res.data.timeline || res.data.history || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [dt.id]);

  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-end">
      <div className="bg-white w-full rounded-t-2xl max-h-[80vh] overflow-hidden animate-slide-up">
        {/* Header */}
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between sticky top-0">
          <div>
            <div className="font-bold text-slate-800">{dt.dt_code}</div>
            <div className="text-xs text-slate-500">{dt.feeder_code}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full">
            <X size={20} />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {/* Basic Info */}
          <div className="space-y-3 mb-4">
            <div className="flex justify-between">
              <span className="text-sm text-slate-500">Location</span>
              <span className="text-sm font-medium">{dt.dt_name || dt.location_name || "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-slate-500">Status</span>
              <span className="text-sm font-medium">{dt.lifecycle_status?.replace(/_/g, " ").toUpperCase()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-slate-500">Circle</span>
              <span className="text-sm font-medium">{dt.circle_name}</span>
            </div>
          </div>

          {/* Stage History */}
          <div className="border-t border-slate-200 pt-4">
            <div className="text-xs font-bold text-slate-500 uppercase mb-3">Stage History</div>
            {loading ? (
              <div className="text-center py-4">
                <RefreshCw size={20} className="animate-spin mx-auto text-slate-400" />
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-4 text-slate-400 text-sm">No history available</div>
            ) : (
              <div className="space-y-3">
                {history.map((h, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="w-2 h-2 rounded-full bg-orange-500 mt-1.5" />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-slate-800">{h.stage || h.event || h.status}</div>
                      <div className="text-xs text-slate-500">
                        {h.completed_by || h.user || "—"} • {h.completed_at ? new Date(h.completed_at).toLocaleString() : "—"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Notifications Modal
function NotificationsModal({ notifications, onClose }) {
  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-end">
      <div className="bg-white w-full rounded-t-2xl max-h-[70vh] overflow-hidden animate-slide-up">
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between sticky top-0">
          <div className="font-bold text-slate-800">Notifications</div>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full">
            <X size={20} />
          </button>
        </div>
        
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {notifications.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Bell size={32} className="mx-auto mb-2" />
              <div>No notifications</div>
            </div>
          ) : (
            <div className="space-y-3">
              {notifications.map((n, i) => (
                <div key={i} className="p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle size={18} className="text-orange-500 mt-0.5" />
                    <div>
                      <div className="font-medium text-sm text-slate-800">{n.title || n.subject}</div>
                      <div className="text-xs text-slate-500 mt-1">{n.message || n.content}</div>
                      <div className="text-[10px] text-slate-400 mt-1">
                        {n.created_at ? new Date(n.created_at).toLocaleString() : "—"}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Start Travel Modal
function StartTravelModal({ onClose, onSuccess }) {
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [startKm, setStartKm] = useState("");
  const [photoBase64, setPhotoBase64] = useState(null);
  const [gps, setGps] = useState(null);
  const [loading, setLoading] = useState(false);
  const [capturingGps, setCapturingGps] = useState(false);
  const fileInputRef = useRef(null);

  // Auto-capture GPS on mount
  useEffect(() => {
    captureGps();
  }, []);

  const captureGps = () => {
    setCapturingGps(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setCapturingGps(false);
      },
      () => {
        toast.error("Could not capture GPS location");
        setCapturingGps(false);
      },
      { enableHighAccuracy: true }
    );
  };

  const handlePhotoCapture = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoBase64(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!vehicleNumber.trim()) {
      toast.error("Please enter vehicle number");
      return;
    }
    if (!startKm || isNaN(parseFloat(startKm))) {
      toast.error("Please enter valid KM reading");
      return;
    }
    if (!photoBase64) {
      toast.error("Odometer photo is mandatory");
      return;
    }
    if (!gps) {
      toast.error("GPS location is required");
      return;
    }

    setLoading(true);
    try {
      await api.post("/travel/start-trip", {
        vehicle_number: vehicleNumber.trim().toUpperCase(),
        start_km: parseFloat(startKm),
        start_gps_lat: gps.lat,
        start_gps_lng: gps.lng,
        start_odometer_photo: photoBase64
      });
      toast.success("Travel started!");
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start travel");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-end">
      <div className="bg-white w-full rounded-t-2xl max-h-[90vh] overflow-hidden animate-slide-up">
        <div className="px-4 py-3 bg-blue-500 text-white flex items-center justify-between sticky top-0">
          <div className="font-bold">Start Travel</div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full">
            <X size={20} />
          </button>
        </div>
        
        <div className="p-4 overflow-y-auto max-h-[75vh] space-y-4">
          {/* Vehicle Number */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Vehicle Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={vehicleNumber}
              onChange={(e) => setVehicleNumber(e.target.value)}
              placeholder="e.g., UP32AB1234"
              className="w-full px-3 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 uppercase"
              data-testid="vehicle-number-input"
            />
          </div>

          {/* Start KM */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Odometer Reading (KM) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={startKm}
              onChange={(e) => setStartKm(e.target.value)}
              placeholder="Enter current KM reading"
              className="w-full px-3 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="start-km-input"
            />
          </div>

          {/* GPS Location */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              GPS Location <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-xl">
              {capturingGps ? (
                <RefreshCw size={20} className="animate-spin text-blue-500" />
              ) : gps ? (
                <MapPin size={20} className="text-green-500" />
              ) : (
                <MapPin size={20} className="text-slate-400" />
              )}
              <span className={`text-sm ${gps ? "text-green-600" : "text-slate-500"}`}>
                {capturingGps ? "Capturing..." : gps ? `${gps.lat.toFixed(6)}, ${gps.lng.toFixed(6)}` : "Not captured"}
              </span>
              <button
                onClick={captureGps}
                className="ml-auto px-2 py-1 text-xs bg-blue-100 text-blue-600 rounded font-medium"
              >
                Refresh
              </button>
            </div>
          </div>

          {/* Odometer Photo */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Odometer Photo <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              ref={fileInputRef}
              onChange={handlePhotoCapture}
              className="hidden"
            />
            {photoBase64 ? (
              <div className="relative">
                <img src={photoBase64} alt="Odometer" className="w-full h-40 object-cover rounded-xl" />
                <button
                  onClick={() => setPhotoBase64(null)}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full py-8 border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center text-slate-500 hover:border-blue-500 hover:text-blue-500"
                data-testid="capture-start-photo-btn"
              >
                <Camera size={32} className="mb-2" />
                <span className="text-sm font-medium">Tap to capture odometer photo</span>
              </button>
            )}
          </div>

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full py-4 bg-blue-500 text-white rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="submit-start-travel-btn"
          >
            {loading ? (
              <RefreshCw size={20} className="animate-spin" />
            ) : (
              <>
                <Play size={20} />
                Start Travel
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// End Travel Modal
function EndTravelModal({ trip, onClose, onSuccess }) {
  const [endKm, setEndKm] = useState("");
  const [photoBase64, setPhotoBase64] = useState(null);
  const [gps, setGps] = useState(null);
  const [loading, setLoading] = useState(false);
  const [capturingGps, setCapturingGps] = useState(false);
  const fileInputRef = useRef(null);

  // Auto-capture GPS on mount
  useEffect(() => {
    captureGps();
  }, []);

  const captureGps = () => {
    setCapturingGps(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setCapturingGps(false);
      },
      () => {
        toast.error("Could not capture GPS location");
        setCapturingGps(false);
      },
      { enableHighAccuracy: true }
    );
  };

  const handlePhotoCapture = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoBase64(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!endKm || isNaN(parseFloat(endKm))) {
      toast.error("Please enter valid end KM reading");
      return;
    }
    if (parseFloat(endKm) < trip.start_km) {
      toast.error("End KM cannot be less than start KM");
      return;
    }
    if (!photoBase64) {
      toast.error("Odometer photo is mandatory");
      return;
    }
    if (!gps) {
      toast.error("GPS location is required");
      return;
    }

    setLoading(true);
    try {
      await api.post(`/travel/end-trip/${trip.id}`, {
        end_km: parseFloat(endKm),
        end_gps_lat: gps.lat,
        end_gps_lng: gps.lng,
        end_odometer_photo: photoBase64
      });
      toast.success("Travel ended!");
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to end travel");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-end">
      <div className="bg-white w-full rounded-t-2xl max-h-[90vh] overflow-hidden animate-slide-up">
        <div className="px-4 py-3 bg-orange-500 text-white flex items-center justify-between sticky top-0">
          <div className="font-bold">End Travel</div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full">
            <X size={20} />
          </button>
        </div>
        
        <div className="p-4 overflow-y-auto max-h-[75vh] space-y-4">
          {/* Trip Info */}
          <div className="p-3 bg-orange-50 rounded-xl">
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Vehicle:</span>
              <span className="font-bold text-slate-800">{trip.vehicle_number}</span>
            </div>
            <div className="flex justify-between text-sm mt-1">
              <span className="text-slate-600">Start KM:</span>
              <span className="font-bold text-slate-800">{trip.start_km} km</span>
            </div>
          </div>

          {/* End KM */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              End Odometer Reading (KM) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={endKm}
              onChange={(e) => setEndKm(e.target.value)}
              placeholder="Enter current KM reading"
              className="w-full px-3 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              data-testid="end-km-input"
            />
            {endKm && parseFloat(endKm) > trip.start_km && (
              <div className="mt-1 text-sm text-green-600">
                Distance: {(parseFloat(endKm) - trip.start_km).toFixed(1)} km
              </div>
            )}
          </div>

          {/* GPS Location */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              GPS Location <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-xl">
              {capturingGps ? (
                <RefreshCw size={20} className="animate-spin text-orange-500" />
              ) : gps ? (
                <MapPin size={20} className="text-green-500" />
              ) : (
                <MapPin size={20} className="text-slate-400" />
              )}
              <span className={`text-sm ${gps ? "text-green-600" : "text-slate-500"}`}>
                {capturingGps ? "Capturing..." : gps ? `${gps.lat.toFixed(6)}, ${gps.lng.toFixed(6)}` : "Not captured"}
              </span>
              <button
                onClick={captureGps}
                className="ml-auto px-2 py-1 text-xs bg-orange-100 text-orange-600 rounded font-medium"
              >
                Refresh
              </button>
            </div>
          </div>

          {/* Odometer Photo */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Odometer Photo <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              ref={fileInputRef}
              onChange={handlePhotoCapture}
              className="hidden"
            />
            {photoBase64 ? (
              <div className="relative">
                <img src={photoBase64} alt="Odometer" className="w-full h-40 object-cover rounded-xl" />
                <button
                  onClick={() => setPhotoBase64(null)}
                  className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full py-8 border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center text-slate-500 hover:border-orange-500 hover:text-orange-500"
                data-testid="capture-end-photo-btn"
              >
                <Camera size={32} className="mb-2" />
                <span className="text-sm font-medium">Tap to capture odometer photo</span>
              </button>
            )}
          </div>

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full py-4 bg-orange-500 text-white rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="submit-end-travel-btn"
          >
            {loading ? (
              <RefreshCw size={20} className="animate-spin" />
            ) : (
              <>
                <Square size={20} />
                End Travel
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// Travel History Modal
function TravelHistoryModal({ onClose }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));

  useEffect(() => {
    const fetchHistory = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/travel/my-history?month=${month}`);
        setLogs(res.data.logs || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [month]);

  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-end">
      <div className="bg-white w-full rounded-t-2xl max-h-[85vh] overflow-hidden animate-slide-up">
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between sticky top-0">
          <div className="font-bold text-slate-800">Travel History</div>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full">
            <X size={20} />
          </button>
        </div>
        
        {/* Month Selector */}
        <div className="px-4 py-3 border-b border-slate-100">
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
        
        <div className="p-4 overflow-y-auto max-h-[65vh]">
          {loading ? (
            <div className="text-center py-8">
              <RefreshCw size={24} className="animate-spin mx-auto text-slate-400" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Car size={32} className="mx-auto mb-2" />
              <div>No travel logs for this month</div>
            </div>
          ) : (
            <div className="space-y-3">
              {logs.map((log, i) => (
                <div key={i} className="p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-bold text-slate-800">{log.log_date || log.date}</span>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                      log.status === "approved" ? "bg-green-100 text-green-700" :
                      log.status === "rejected" ? "bg-red-100 text-red-700" :
                      "bg-yellow-100 text-yellow-700"
                    }`}>
                      {log.status || "pending"}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-slate-500">Vehicle:</span>
                      <span className="ml-1 font-medium">{log.vehicle_number || "—"}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Distance:</span>
                      <span className="ml-1 font-medium">{log.total_road_km || log.distance_km || 0} km</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
